import { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import Top8Display from './Top8Display';
import './Top8Standings.css';

export default function Top8Standings() {
  const [standings, setStandings] = useState(
    Array.from({ length: 8 }, (_, i) => ({
      rank: i + 1,
      player: '',
      team: ['', '', '', '', '', ''],
      score: '',
    }))
  );
  const [tournamentName, setTournamentName] = useState('');
  const [playerCount, setPlayerCount] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const standingsRef = useRef(null);

  const handlePlayerChange = (index, value) => {
    const updated = [...standings];
    updated[index].player = value;
    setStandings(updated);
  };

  const handleScoreChange = (index, value) => {
    const updated = [...standings];
    updated[index].score = value;
    setStandings(updated);
  };

  const handlePokemonChange = (rankIndex, pokemonIndex, value) => {
    const updated = [...standings];
    updated[rankIndex].team[pokemonIndex] = value;
    setStandings(updated);
  };

  const handleExport = async () => {
    if (!standingsRef.current) return;

    try {
      setExportLoading(true);

      // Patch styles before capture to fix html2canvas rendering issues
      const entries = standingsRef.current.querySelectorAll('.top8-entry');
      const playerSections = standingsRef.current.querySelectorAll('.top8-entry__player-section');
      const playerNames = standingsRef.current.querySelectorAll('.top8-entry__player-name');
      const winnerEntry = standingsRef.current.querySelector('.top8-entry--winner');

      const originalEntryStyles = [];
      const originalPlayerSectionStyles = [];
      const originalPlayerNameStyles = [];
      let originalWinnerBackground = null;
      let originalWinnerBoxShadow = null;
      let originalWinnerBorder = null;

      entries.forEach((el) => {
        originalEntryStyles.push({
          paddingTop: el.style.paddingTop,
        });
        el.style.paddingTop = '1.5rem';
      });

      playerSections.forEach((el) => {
        originalPlayerSectionStyles.push({
          marginLeft: el.style.marginLeft,
          paddingTop: el.style.paddingTop,
          marginBottom: el.style.marginBottom,
        });
        el.style.marginLeft = '52px';
        el.style.paddingTop = '0px';
        el.style.marginBottom = '0.75rem';
      });

      playerNames.forEach((el) => {
        originalPlayerNameStyles.push({
          lineHeight: el.style.lineHeight,
          overflow: el.style.overflow,
        });
        el.style.lineHeight = '1.2';
        el.style.overflow = 'visible';
      });

      if (winnerEntry) {
        originalWinnerBackground = winnerEntry.style.background;
        originalWinnerBoxShadow = winnerEntry.style.boxShadow;
        originalWinnerBorder = winnerEntry.style.border;
        winnerEntry.style.background = 'linear-gradient(135deg, rgba(53, 14, 181, 0.35), rgba(140, 212, 193, 0.18))';
        winnerEntry.style.border = '2px solid rgba(140, 212, 193, 0.8)';
        winnerEntry.style.boxShadow = '0 0 32px rgba(53, 14, 181, 0.4), 0 0 16px rgba(140, 212, 193, 0.25)';
      }

      const canvas = await html2canvas(standingsRef.current, {
        backgroundColor: '#0f0243',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
        onclone: (clonedDoc) => {
          // Fix any remaining issues in the cloned document
          const clonedEntries = clonedDoc.querySelectorAll('.top8-entry');
          clonedEntries.forEach((el) => {
            el.style.overflow = 'visible';
            el.style.paddingTop = '1.5rem';
          });

          const clonedPlayerNames = clonedDoc.querySelectorAll('.top8-entry__player-name');
          clonedPlayerNames.forEach((el) => {
            el.style.lineHeight = '1.2';
            el.style.overflow = 'visible';
            el.style.whiteSpace = 'nowrap';
          });

          const clonedPlayerSections = clonedDoc.querySelectorAll('.top8-entry__player-section');
          clonedPlayerSections.forEach((el) => {
            el.style.marginLeft = '52px';
            el.style.paddingTop = '0px';
            el.style.overflow = 'visible';
          });

          const clonedWinner = clonedDoc.querySelector('.top8-entry--winner');
          if (clonedWinner) {
            clonedWinner.style.background = 'linear-gradient(135deg, rgba(53, 14, 181, 0.35), rgba(140, 212, 193, 0.18))';
            clonedWinner.style.border = '2px solid rgba(140, 212, 193, 0.8)';
            clonedWinner.style.boxShadow = '0 0 32px rgba(53, 14, 181, 0.4), 0 0 16px rgba(140, 212, 193, 0.25)';
          }
        },
      });

      // Restore original styles
      entries.forEach((el, i) => {
        el.style.paddingTop = originalEntryStyles[i].paddingTop;
      });
      playerSections.forEach((el, i) => {
        el.style.marginLeft = originalPlayerSectionStyles[i].marginLeft;
        el.style.paddingTop = originalPlayerSectionStyles[i].paddingTop;
        el.style.marginBottom = originalPlayerSectionStyles[i].marginBottom;
      });
      playerNames.forEach((el, i) => {
        el.style.lineHeight = originalPlayerNameStyles[i].lineHeight;
        el.style.overflow = originalPlayerNameStyles[i].overflow;
      });
      if (winnerEntry) {
        winnerEntry.style.background = originalWinnerBackground;
        winnerEntry.style.boxShadow = originalWinnerBoxShadow;
        winnerEntry.style.border = originalWinnerBorder;
      }

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `top8-standings-${new Date().getTime()}.png`;
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
      alert("Erreur lors de l'export de l'image");
    } finally {
      setExportLoading(false);
    }
  };

  // Filter out empty standings for display
  const filledStandings = standings.filter((s) => s.player.trim() !== '');

  return (
    <div className="top8-standings">
      <section className="top8-standings__input section">
        <div className="container">
          <h2 className="top8-standings__title staatliches-regular">Top 8 - Outil de Classement</h2>
          <p className="top8-standings__subtitle chiron-goround-tc-body">
            Entrez les joueurs et leurs equipes Pokémon pour créer un classement.
          </p>

          <form className="top8-standings__form">
            <div className="top8-standings__tournament-info">
              <div className="top8-standings__info-field">
                <label htmlFor="tournament-name" className="top8-standings__label chiron-goround-tc-light">
                  Nom du Tournoi
                </label>
                <input
                  id="tournament-name"
                  type="text"
                  className="top8-standings__input-field"
                  placeholder="Ex: Championnat Regional 2026"
                  value={tournamentName}
                  onChange={(e) => setTournamentName(e.target.value)}
                />
              </div>

              <div className="top8-standings__info-field">
                <label htmlFor="player-count" className="top8-standings__label chiron-goround-tc-light">
                  Nombre de Joueurs
                </label>
                <input
                  id="player-count"
                  type="number"
                  className="top8-standings__input-field"
                  placeholder="Ex: 64"
                  value={playerCount}
                  onChange={(e) => setPlayerCount(e.target.value)}
                />
              </div>
            </div>

            {standings.map((entry, rankIndex) => (
              <fieldset
                key={entry.rank}
                className="top8-standings__entry top8-standings__entry--input card"
              >
                <div className="top8-standings__rank-label">#{entry.rank}</div>

                <div className="top8-standings__field">
                  <label htmlFor={`player-${entry.rank}`} className="top8-standings__label chiron-goround-tc-light">
                    Joueur
                  </label>
                  <input
                    id={`player-${entry.rank}`}
                    type="text"
                    className="top8-standings__input-field"
                    placeholder="Nom du joueur"
                    value={entry.player}
                    onChange={(e) => handlePlayerChange(rankIndex, e.target.value)}
                  />
                </div>

                <div className="top8-standings__field">
                  <label htmlFor={`score-${entry.rank}`} className="top8-standings__label chiron-goround-tc-light">
                    Score (ex: 7-0)
                  </label>
                  <input
                    id={`score-${entry.rank}`}
                    type="text"
                    className="top8-standings__input-field"
                    placeholder="Ex: 7-0"
                    value={entry.score}
                    onChange={(e) => handleScoreChange(rankIndex, e.target.value)}
                  />
                </div>

                <div className="top8-standings__pokemon-grid">
                  {entry.team.map((pokemon, pokeIndex) => (
                    <div
                      key={`${entry.rank}-${pokeIndex}`}
                      className="top8-standings__pokemon-input"
                    >
                      <label
                        htmlFor={`pokemon-${entry.rank}-${pokeIndex}`}
                        className="top8-standings__pokemon-label chiron-goround-tc-light"
                      >
                        Pokémon {pokeIndex + 1}
                      </label>
                      <input
                        id={`pokemon-${entry.rank}-${pokeIndex}`}
                        type="text"
                        className="top8-standings__pokemon-field"
                        placeholder={`Pokémon ${pokeIndex + 1}`}
                        value={pokemon}
                        onChange={(e) => handlePokemonChange(rankIndex, pokeIndex, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>
            ))}
          </form>
        </div>
      </section>

      {/* Preview Section */}
      {filledStandings.length > 0 && (
        <section className="top8-standings__preview section section--dark">
          <div className="container">
            <div className="top8-standings__preview-header">
              <h2 className="top8-standings__preview-title staatliches-regular">Apercu</h2>
              <button
                className="top8-standings__export-btn"
                onClick={handleExport}
                disabled={exportLoading}
              >
                {exportLoading ? 'Export en cours...' : 'Telecharger PNG'}
              </button>
            </div>
            <div ref={standingsRef} className="top8-standings__capture-container">
              <Top8Display
                standings={filledStandings}
                tournamentName={tournamentName}
                playerCount={playerCount}
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}